import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ScreenContainerRef } from '../../context'
import {
  useDate,
  useLinkLikeClick,
  usePageBrowserTabTitle,
  useUserData,
} from '../../hooks'
import { getUserFullName, getUserId } from '../../utils/user'
import { Page } from '../Page'

import { BirdviewAnalytics } from './BirdviewAnalytics'
import { BirdviewControl } from './BirdviewControl'
import { BirdviewDashboardView } from './BirdviewDashboardView'
import { BirdviewHeader } from './BirdviewHeader'
import { BirdviewScope, BirdviewView } from './DashboardBirdview.constants'
import { QA_NAME_DASHBOARD_BIRDVIEW } from './DashboardBirdview.constants.qa'
import { BirdviewHeaderActionsContainer } from './DashboardBirdview.context'
import {
  useBirdviewAnalyticsLoaders,
  useBirdviewQuickActions,
  useBirdviewRequestQuickActions,
  useBirdviewSectionLoaders,
  useDashboardBirdviewSections,
} from './DashboardBirdview.hooks'
import {
  checkIsBirdviewAnalyticsView,
  checkIsBirdviewDwellerSkeleton,
  checkIsBirdviewOversightSkeleton,
  checkIsBirdviewSectionsLoading,
  getBirdviewAnalyticsPreset,
  getBirdviewEffectiveScope,
  getBirdviewEffectiveView,
  getBirdviewRole,
} from './DashboardBirdview.utils'
import {
  clearBirdviewStoredLayout,
  getBirdviewLayoutStorageKeys,
  getBirdviewStorageKey,
  getBirdviewStorageScope,
} from './DashboardBirdview.utils.storage'

import * as styles from './DashboardBirdview.module.css'

function DashboardBirdview() {
  const { t } = useTranslation()

  usePageBrowserTabTitle({ pageTitle: t('BrowserTab.DashboardPageTitleKey') })

  const user = useUserData()

  const [view, setView] = useState(BirdviewView.Dashboard)
  const [scope, setScope] = useState(BirdviewScope.Mine)
  const [modal, setModal] = useState(null)
  const [layoutToken, setLayoutToken] = useState(0)
  const [headerActionsContainer, setHeaderActionsContainer] = useState(null)

  const dashboardRef = useRef(null)

  const date = useDate()

  const { handleClick } = useLinkLikeClick()

  const userId = getUserId(user)
  const role = getBirdviewRole(user.role)
  const effectiveScope = getBirdviewEffectiveScope({ role, scope })
  const effectiveView = getBirdviewEffectiveView({ role, view })
  const isAnalyticsView = checkIsBirdviewAnalyticsView(effectiveView)
  const storageScope = getBirdviewStorageScope({ role: role.value, userId })
  const preset = getBirdviewAnalyticsPreset(role)
  const layoutKey = getBirdviewStorageKey(storageScope, [layoutToken])

  const { getters: dashboardGetters, loadAttentionGroup } =
    useBirdviewSectionLoaders(role)
  const { getters: analyticsGetters } = useBirdviewAnalyticsLoaders()

  function handleCloseModal() {
    setModal(null)
  }

  const {
    activityDoneById,
    handleActivityToggle,
    handleResetActivityDone,
    pendingIds,
  } = useBirdviewQuickActions()

  const {
    assigneeById,
    handleAssign,
    handleResetAssignee,
    pendingIds: pendingRequestIds,
  } = useBirdviewRequestQuickActions({
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  function handleResetQuickActions(sectionKey) {
    handleResetActivityDone(sectionKey)
    handleResetAssignee(sectionKey)
  }

  // One sections store serves both views, so switching back finds them loaded.
  // The preset keys the requests, so a role switch reloads the KPIs it picks.
  const { reload, sections } = useDashboardBirdviewSections({
    getters: isAnalyticsView ? analyticsGetters : dashboardGetters,
    params: { preset, scope: effectiveScope, userId },
    onReload: handleResetQuickActions,
  })

  const requestActions = {
    assigneeById,
    pendingIds: pendingRequestIds,
    onAssign: handleAssign,
  }

  // Profiles the NG app has no page for yet resolve to old-webapp links, which
  // the router cannot serve: the link-like handler opens those in place.
  function handleNavigate(link) {
    handleClick({ link })
  }

  // The boards read the stored layout once, so a reset has to remount them.
  function handleResetLayout() {
    clearBirdviewStoredLayout(
      getBirdviewLayoutStorageKeys({ isAnalyticsView, storageScope })
    )
    setLayoutToken(token => token + 1)
  }

  async function handleLoadAttentionGroup({ age, group }) {
    return loadAttentionGroup({ age, group, scope: effectiveScope, userId })
  }

  return (
    <Page qaName={QA_NAME_DASHBOARD_BIRDVIEW}>
      <ScreenContainerRef value={dashboardRef}>
        <BirdviewHeaderActionsContainer value={headerActionsContainer}>
          <div className={styles.dashboard} ref={dashboardRef}>
            <BirdviewHeader
              ScopeControl={
                checkIsBirdviewOversightSkeleton(role) ? BirdviewControl : null
              }
              ViewControl={
                checkIsBirdviewDwellerSkeleton(role) ? null : BirdviewControl
              }
              actionsContainerRef={setHeaderActionsContainer}
              date={date}
              greetingName={getUserFullName(user)}
              isRefreshing={checkIsBirdviewSectionsLoading(sections)}
              scope={effectiveScope}
              view={effectiveView}
              onChangeScope={setScope}
              onChangeView={setView}
              onRefresh={reload}
              onResetLayout={isAnalyticsView ? null : handleResetLayout}
            />

            {isAnalyticsView ? (
              <BirdviewAnalytics
                key={layoutKey}
                preset={preset}
                scope={effectiveScope}
                sections={sections}
                storageScope={storageScope}
                onNavigate={handleNavigate}
                onResetLayout={handleResetLayout}
              />
            ) : (
              <BirdviewDashboardView
                activityDoneById={activityDoneById}
                key={layoutKey}
                pendingIds={pendingIds}
                requestActions={requestActions}
                role={role}
                scope={effectiveScope}
                sections={sections}
                storageScope={storageScope}
                onActivityToggle={handleActivityToggle}
                onLoadAttentionGroup={handleLoadAttentionGroup}
                onNavigate={handleNavigate}
              />
            )}
          </div>
        </BirdviewHeaderActionsContainer>
      </ScreenContainerRef>
      {modal}
    </Page>
  )
}

export default DashboardBirdview
